// DaySummaryQuickView — right-panel detail for the attendance / equipment
// day-summary badges (a click on "N on site" resolves to the actual list of
// people / plant on site that day). Reuses existing list endpoints; no new
// backend endpoints required.
import { useEffect, useState } from "react";
import { Loader2, ArrowLeft, Users, Truck, CalendarOff } from "lucide-react";
import { api } from "@/lib/api";

export default function DaySummaryQuickView({ item, onBack }) {
    const isAttendance = item.source_module === "attendance";
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        (async () => {
            try {
                if (isAttendance) {
                    const params = { date: item.date, client_id: item.client_id };
                    if (item.project_id) params.project_id = item.project_id;
                    const { data } = await api.get("/site-forms/attendance", { params });
                    if (alive) setRows(data || []);
                } else {
                    const { data } = await api.get("/equipment", { params: { project_id: item.project_id } });
                    const onSite = (data || []).filter((eq) => {
                        if (eq.on_hire_date && eq.on_hire_date.slice(0, 10) > item.date) return false;
                        if (eq.off_hire_date && eq.off_hire_date.slice(0, 10) < item.date) return false;
                        return true;
                    });
                    if (alive) setRows(onSite);
                }
            } catch {
                if (alive) setRows([]);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [isAttendance, item.date, item.client_id, item.project_id]);

    const dayLabel = new Date(item.date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

    return (
        <div className="rounded-2xl bg-white border border-[#E5E0DA] p-5" data-testid="calendar-day-summary">
            <button onClick={onBack} data-testid="calendar-summary-back"
                className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#6F6A64] hover:text-[#1F1F1F] mb-3">
                <ArrowLeft size={12} /> Back to day
            </button>
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] flex items-center gap-1.5">
                {isAttendance ? <Users size={13} /> : <Truck size={13} />}
                {isAttendance ? "People signed in" : "Plant / vehicles on site"}
            </h4>
            <p className="text-[12px] text-[#9A968F] mt-0.5">{dayLabel}</p>

            <div className="mt-3 space-y-2" data-testid="calendar-day-summary-list">
                {loading && <Loader2 className="animate-spin text-[#9A968F]" size={16} />}
                {!loading && rows.length === 0 && (
                    <div className="text-[12px] italic text-[#9A968F] flex items-center gap-2 py-4">
                        <CalendarOff size={14} /> No records found.
                    </div>
                )}
                {!loading && rows.map((r) => (
                    <div key={r.id} data-testid={`calendar-day-summary-row-${r.id}`}
                        className="rounded-lg border border-[#F0EDE8] p-2.5">
                        {isAttendance ? (
                            <>
                                <div className="text-[13px] font-semibold text-[#1F1F1F]">{r.name}</div>
                                <div className="text-[10.5px] text-[#9A968F] mt-0.5">
                                    {r.company || r.person_type || "—"} · {(r.time_in || "").slice(11, 16)}
                                    {r.time_out ? ` – ${r.time_out.slice(11, 16)}` : " (still on site)"}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="text-[13px] font-semibold text-[#1F1F1F]">{r.item}</div>
                                <div className="text-[10.5px] text-[#9A968F] mt-0.5">
                                    {r.category || "—"} · {r.plant_no || "no plant no."} · {r.status}
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
